import { cache } from 'react';

import {
	REQUEST_ID_HEADER,
	SSR_DEBUG_HEADER,
} from '@/shared/utils/logger/constants';

export type SsrDebugEntry = {
	request: {
		method: string;
		url: string;
		requestId?: string;
		body?: unknown;
	};
	response: {
		status: number | null;
		ok: boolean | null;
		durationMs: number;
		body: unknown;
		error?: string;
	};
};

const MAX_BODY_CHARS = 80_000;

type SsrDebugStore = {
	enabled: boolean | null;
	entries: SsrDebugEntry[];
};

/** Request-scoped буфер (React `cache`) — общий для layout/page в одном SSR. */
export const getSsrDebugStore = cache(
	(): SsrDebugStore => ({
		enabled: null,
		entries: [],
	}),
);

export async function isSsrDebugEnabled(): Promise<boolean> {
	const store = getSsrDebugStore();
	if (store.enabled != null) return store.enabled;

	try {
		const { headers } = await import('next/headers');
		const h = await headers();
		store.enabled = isTruthyFlag(h.get(SSR_DEBUG_HEADER));
	} catch {
		store.enabled = false;
	}

	return store.enabled;
}

function isTruthyFlag(value: string | null | undefined): boolean {
	if (!value) return false;
	const v = value.trim().toLowerCase();
	return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

export function pushSsrDebugEntry(entry: SsrDebugEntry): void {
	getSsrDebugStore().entries.push(entry);
}

export function getSsrDebugEntries(): SsrDebugEntry[] {
	return getSsrDebugStore().entries;
}

export function parseRequestBody(body: BodyInit | null | undefined): unknown {
	if (body == null) return undefined;
	if (typeof body === 'string') {
		try {
			return truncateJson(JSON.parse(body));
		} catch {
			return truncateText(body);
		}
	}
	return '[non-string body]';
}

export async function parseDebugBody(response: Response): Promise<unknown> {
	const contentType = response.headers.get('content-type') ?? '';
	try {
		if (contentType.includes('application/json')) {
			return truncateJson(await response.json());
		}
		return truncateText(await response.text());
	} catch (error) {
		return {
			parseError: error instanceof Error ? error.message : String(error),
		};
	}
}

function truncateText(text: string): string {
	return text.length > MAX_BODY_CHARS
		? `${text.slice(0, MAX_BODY_CHARS)}… [truncated ${text.length} chars]`
		: text;
}

function truncateJson(value: unknown): unknown {
	try {
		const raw = JSON.stringify(value);
		if (raw.length <= MAX_BODY_CHARS) return value;
		return {
			__truncated: true,
			originalLength: raw.length,
			preview: raw.slice(0, MAX_BODY_CHARS),
		};
	} catch {
		return value;
	}
}

export function pickRequestId(headers: Headers): string | undefined {
	return headers.get(REQUEST_ID_HEADER) ?? undefined;
}
